import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { CatchDataStatisticsResponseDto, EntityStatisticsResponseDto } from '@core/api';
import { environment } from '@env';
import { CustomDatePipe } from '@shared/pipes/custom-date.pipe';
import {
  ChartConfiguration,
  ChartType,
  Chart,
  LinearScale,
  CategoryScale,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PieController
} from 'chart.js';
import { format } from 'date-fns';

Chart.register(
  LinearScale,
  CategoryScale,
  BarElement,
  BarController,
  ArcElement,
  PieController,
  Title,
  Tooltip,
  Legend
);

type ChartData = EntityStatisticsResponseDto[] | CatchDataStatisticsResponseDto[];

@Component({
  selector: 'ldc-chart',
  templateUrl: './chart.component.html',
  styleUrl: './chart.component.scss'
})
export class ChartComponent implements OnInit, OnChanges {
  @Input() chartData: ChartData = [];
  @Input() yAxisLabel: string = 'Count';
  @Input() xAxisLabel: string = 'Date';
  @Input() chartType: ChartType = 'bar';
  @Input() showLegend: boolean = false;
  @Output() dateRangeChange: EventEmitter<{ startDate: string; endDate: string }> = new EventEmitter();

  dateFormat = environment.dateFormatWithoutTime;

  chartDataConfig: ChartConfiguration['data'] = {
    datasets: [],
    labels: []
  };
  chartOptions: ChartConfiguration['options'] = {};

  dateRange: Date[] = [];
  startDate: Date = new Date();
  endDate: Date = new Date();

  private readonly colors: string[] = [
    '#6db19a',
    '#4a90e2',
    '#e6a243',
    '#d66853',
    '#9b6b9d',
    '#60b3c1',
    '#d4826a',
    '#7f8fa3',
    '#97a878',
    '#cf9f4f'
  ];

  constructor(private customDatePipe: CustomDatePipe) {}

  ngOnInit(): void {
    this.endDate = new Date();
    this.startDate = new Date();
    this.startDate.setDate(this.endDate.getDate() - 30); // Default to last 30 days
    this.dateRange = [this.startDate, this.endDate];

    this.emitFormattedDateRange();

    this.updateChartData();
  }

  ngOnChanges(): void {
    this.updateChartData();
  }

  onDateRangeChange(dateRange: Date[]): void {
    this.dateRange = dateRange;

    this.startDate = dateRange[0];
    this.endDate = dateRange[1];

    this.emitFormattedDateRange();
  }

  updateChartData(): void {
    let labels: string[] = [];
    let data: number[] = [];

    if (this.isEntityStatistics(this.chartData)) {
      labels = this.chartData
        .filter(item => item.periodName)
        .map(item => this.customDatePipe.transform(item.periodName!, this.dateFormat) || '');

      data = this.chartData.map(item => item.count || 0);
    } else if (this.isCatchDataStatistics(this.chartData)) {
      labels = this.chartData.map(item =>
        item.marineOrganismType?.name ? item.marineOrganismType.name : item.marineOrganismType?.code || 'Unknown'
      );

      data = this.chartData.map(item => item.weight || 0);
    }

    this.chartDataConfig = {
      labels: labels,
      datasets: [
        {
          data: data,
          label: this.chartType === 'bar' ? this.yAxisLabel : undefined,
          backgroundColor: this.chartType === 'pie' ? this.colors.slice(0, data.length) : this.colors[0]
        }
      ]
    };

    this.updateChartOptions();
  }

  private updateChartOptions(): void {
    if (this.chartType === 'pie') {
      this.chartOptions = {
        responsive: true,
        plugins: {
          legend: {
            position: 'right',
            display: true
          }
        }
      };
    } else {
      this.chartOptions = {
        responsive: true,
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: this.yAxisLabel
            },
            ticks: { stepSize: 1 }
          },
          x: {
            title: {
              display: true,
              text: this.xAxisLabel
            }
          }
        },
        plugins: {
          legend: {
            display: this.showLegend
          }
        }
      };
    }
  }

  private isEntityStatistics(data: ChartData): data is EntityStatisticsResponseDto[] {
    return data.length === 0 || 'periodName' in data[0];
  }

  private isCatchDataStatistics(data: ChartData): data is CatchDataStatisticsResponseDto[] {
    return data.length === 0 || 'marineOrganismType' in data[0];
  }

  private emitFormattedDateRange(): void {
    const formattedStartDate = format(this.startDate, 'yyyy-MM-dd');
    const formattedEndDate = format(this.endDate, 'yyyy-MM-dd');

    this.dateRangeChange.emit({
      startDate: formattedStartDate,
      endDate: formattedEndDate
    });
  }
}
